.wrapper {
  outline: none;
  border: none;
}

.dropLine {
  height: 4px;
  border-radius: var(--tuna-shape-radius-sm);
  margin-top: 4px;
  margin-bottom: 4px;
  pointer-events: none;
  background: var(--tuna-palette-primary-main);
  box-shadow: 0 0 8px color-mix(in oklch, var(--tuna-palette-primary-main) 40%, transparent);
  animation: dropLinePulse 1.2s ease-in-out infinite;
}

@keyframes dropLinePulse {
  0%,
  100% {
    opacity: 0.7;
  }
  50% {
    opacity: 1;
  }
}

.block {
  position: relative;
  align-items: center;
  vertical-align: bottom;
  isolation: isolate;
}

/* Where the block's handle sits (block-gutter.module.css): centred on a 24px first line by default. A block type
 * overrides these on its own class; :where() keeps the defaults at zero specificity so it wins, and every nested
 * block re-declares them, so a section's 36px header row never leaks into the audio block inside it. */
:where(.block) {
  --block-handle-line-height: 24px;
  --block-handle-inset-top: 0px;
  --block-handle-inset-right: 0px;
  --block-handle-offset: 4px;
  --block-handle-reserve: 44px;
}

/* On touch and narrow screens the options button sits inside the block at its top right. Reserve its 44px so the
 * block's own controls (an embed's "dismiss", a player's buttons) never sit under it. A block that hosts the button
 * in a row of its own (a section's header) sets --block-handle-reserve to its normal padding. */
.block:has(> [data-block-handle='compact']) {
  padding-right: var(--block-handle-reserve);
}

/* Extend the block's hover region across the whole gap to its margin handle (32px of container padding), so moving
 * the pointer from the content onto the handle never crosses a strip where the block loses :hover and the handle
 * fades out mid-reach. Pointer devices only: elsewhere the control sits inside the block. */
@media (hover: hover) and (pointer: fine) and (min-width: 600px) {
  .block::before {
    content: '';
    position: absolute;
    top: 0;
    left: -32px;
    width: 32px;
    height: 100%;
  }
}

/* A range reads as one band (GH #2730): text and selected chord chips share one tint, the chip's 20%
 * (simple-chord.module.css), and text keeps its own colour, so a chord's in-key colour survives the selection.
 * The opaque token with white text made lyrics a solid block beside pale chips. */
.block ::selection {
  background-color: color-mix(in srgb, var(--tuna-palette-selection) 20%, transparent);
}

/* Slate's zero-width text beside inline voids and the voids' hidden spacers painted as slivers in a range */
.block [data-slate-zero-width='z']::selection,
.block [data-slate-spacer]::selection,
.block [data-slate-spacer] ::selection {
  background-color: transparent;
}

/* Block-level range-selection feedback intentionally omitted.
 *
 * `useIsInSelectionRange` continues to set `data-in-range='true'` on blocks strictly
 * inside a multi-block selection, but no styling is bound to it — multi-block range
 * painting is the least-valuable selection affordance for Tunasong and its backing
 * subscription chain (selection-store ↔ slate-yjs onChange wrapper) has a known
 * staleness bug. Removing only the visual leaves the JS path intact so the bug can
 * be fixed without re-introducing styling. Focus state still differentiates via
 * `.block--focused` (4px right border); section-level cursor presence via
 * `.section--selected` in plugins/section/src/block.module.css. */

.block--inline {
  display: inline-flex;
}

.block--border {
  border-style: solid;
  border-color: var(--tuna-border-default);
  border-radius: var(--tuna-shape-borderRadius, 4px);
  border-width: 0.8px;
}

.block--focused {
  transition: border-color 0.3s;
  border-color: var(--tuna-palette-primary-main);
  border-right-width: 4px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.block--dragOver {
  background: color-mix(in oklch, var(--tuna-palette-primary-main) 4%, transparent);
  border-radius: var(--tuna-shape-borderRadius, 4px);
}

.block--focusRight {
  transition: border-color 0.3s;
  border-top-style: none;
  border-bottom-style: none;
  border-left-style: none;
  border-right-style: solid;
  border-top-width: 0;
  border-bottom-width: 0;
  border-left-width: 0;
  border-right-width: 4px;
  border-right-color: var(--tuna-palette-primary-main);
  border-radius: 0;
}

/* ============================================================
 * Personal-note anchor highlights
 *
 * Two states share one visual axis (left-edge inset shadow), and the stronger
 * overrides the weaker by cascade order: anchored-hover ≺ anchored-active.
 * The active state additionally exposes `--anchor-active: 1` so child
 * elements (anchor markers) can interpolate their own appearance against the
 * same source of truth.
 *
 * A block with a note has no resting halo: the note marker at the line's end
 * already says so. The halo was on whenever the note existed, not only with My
 * Notes open, and at 25% it read as a stray rule before the first character.
 * ============================================================ */

/* Hover preview from the sidebar, so the user can scan rows and see "this is
 * where this note points" without activating the row. Cleared on mouseleave/blur. */
.block[data-anchored-hover='true'] {
  box-shadow: inset 3px 0 0 0 color-mix(in oklch, var(--tuna-palette-selection) 55%, transparent);
}

/* Active state — the user expanded the note row that targets this block. The
 * left-edge accent uses the full selection color (matching the row's accent),
 * and the CSS variable signals descendants (markers, etc.) to lift their own
 * presentation. */
.block[data-anchored-active='true'] {
  box-shadow: inset 3px 0 0 0 var(--tuna-palette-selection);
  --anchor-active: 1;
}

/* One-shot 600ms spotlight applied on activation. The component toggles
 * `data-anchored-pulse` for 600ms then removes it. Reduced-motion users skip
 * the keyframe entirely — they still see the static halo from the active
 * state, just no animated pulse. */
@media (prefers-reduced-motion: no-preference) {
  .block[data-anchored-pulse='true'] {
    animation: anchorSpotlight 600ms ease-out;
  }

  @keyframes anchorSpotlight {
    0% {
      background-color: color-mix(in oklch, var(--tuna-palette-selection) 22%, transparent);
    }
    100% {
      background-color: transparent;
    }
  }
}
